import React, { useState } from 'react';
import {
  Stack,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { IconLibrary } from '@product-framework/index';
import formatTimeZoneDateTime from '@product-framework/utils/formatTimeZoneDateTime';
import { useLocalization, SnackbarApiErrorMessage } from '@ui-framework/index';

function fileName(original) {
  return original?.fileName || '';
}

function createdDateTime(original) {
  const { formatMessage } = useLocalization();

  const { localDatetime, systemDatetime } = formatTimeZoneDateTime(
    original?.createdDateTime,
  );

  return (
    <Stack component="span" spacing={0.25} sx={{ py: 0.25 }}>
      <Typography component="span" variant="body2" sx={{ display: 'block' }}>
        <Typography
          component="span"
          variant="caption"
          color="text.secondary"
          sx={{ mr: 0.75 }}>
          {formatMessage({
            id: 'YOUR_TIME',
            defaultMessage: 'Your Time',
          })}
          :
        </Typography>
        {localDatetime}
      </Typography>
      <Typography component="span" variant="body2" sx={{ display: 'block' }}>
        <Typography
          component="span"
          variant="caption"
          color="text.secondary"
          sx={{ mr: 0.75 }}>
          {formatMessage({
            id: 'SYSTEM_TIME',
            defaultMessage: 'System Time',
          })}
          :
        </Typography>
        {systemDatetime}
      </Typography>
    </Stack>
  );
}

function ClaimPaymentExportDownloadButton({
  row,
  getApiUrl,
  get,
  formatMessage,
  snackbar,
}) {
  const [loading, setLoading] = useState(false);

  const getPresignedUrl = async () => {
    const response = await get(
      `${getApiUrl()}/sxcl/v1/claim-payment-exports/${row?.id}/download`,
    );
    if (response?.ok) {
      return response.data.presignedUrl;
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return null;
  };

  const handleDownload = async () => {
    setLoading(true);
    const presignedUrl = await getPresignedUrl();
    if (presignedUrl) {
      window.open(presignedUrl, '_blank', 'noopener,noreferrer');
    } else {
      snackbar.showMessage({
        type: 'error',
        data: (
          <SnackbarApiErrorMessage
            errors={[
              {
                message: formatMessage({
                  id: 'FAILED_TO_DOWNLOAD_CLAIM_PAYMENT_EXPORT_FILE',
                  defaultMessage:
                    'Failed to download claim payment export file',
                }),
              },
            ]}
          />
        ),
      });
    }
    setLoading(false);
  };

  return (
    <Tooltip
      title={formatMessage({
        id: 'DOWNLOAD_EXPORT_FILE',
        defaultMessage: 'Download export file',
      })}>
      <IconButton size="small" onClick={handleDownload} disabled={loading}>
        {loading ? (
          <CircularProgress color="inherit" size={18} />
        ) : (
          <IconLibrary.FileDownload />
        )}
      </IconButton>
    </Tooltip>
  );
}

function download(original, _props, accessorFunctionsAdditional) {
  const { getApiUrl, get, formatMessage, snackbar } =
    accessorFunctionsAdditional;

  return (
    <ClaimPaymentExportDownloadButton
      row={original}
      getApiUrl={getApiUrl}
      get={get}
      formatMessage={formatMessage}
      snackbar={snackbar}
    />
  );
}

function ClaimPaymentExportPreviewButton({
  row,
  getApiUrl,
  get,
  formatMessage,
  snackbar,
}) {
  const [loading, setLoading] = useState(false);

  const getPresignedUrl = async () => {
    const response = await get(
      `${getApiUrl()}/sxcl/v1/claim-payment-exports/${row?.id}/preview`,
    );
    if (response?.ok) {
      return response.data.presignedUrl;
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return null;
  };

  const handlePreview = async () => {
    setLoading(true);
    const presignedUrl = await getPresignedUrl();
    if (presignedUrl) {
      window.open(presignedUrl, '_blank');
    } else {
      snackbar.showMessage({
        type: 'error',
        data: (
          <SnackbarApiErrorMessage
            errors={[
              {
                message: formatMessage({
                  id: 'FAILED_TO_GENERATE_PREVIEW_OF_EXPORT_FILE',
                  defaultMessage: 'Failed to generate preview of export file',
                }),
              },
            ]}
          />
        ),
      });
    }
    setLoading(false);
  };

  return (
    <Tooltip
      title={formatMessage({
        id: 'OPEN_PREVIEW_IN_A_NEW_TAB',
        defaultMessage: 'Open preview in a new tab',
      })}>
      <IconButton size="small" onClick={handlePreview} disabled={loading}>
        {loading ? (
          <CircularProgress color="inherit" size={18} />
        ) : (
          <IconLibrary.FilePreview />
        )}
      </IconButton>
    </Tooltip>
  );
}

function preview(original, _props, accessorFunctionsAdditional) {
  const { getApiUrl, get, formatMessage, snackbar } =
    accessorFunctionsAdditional;

  return (
    <ClaimPaymentExportPreviewButton
      row={original}
      getApiUrl={getApiUrl}
      get={get}
      formatMessage={formatMessage}
      snackbar={snackbar}
    />
  );
}

export { fileName, createdDateTime, download, preview };
